import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Search, Sparkles, CheckCircle2, Loader2 } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledge';
import type { CanonicalEntityData } from '@/services/api';

function formatLabel(s: string): string {
  return s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function sourceNamesFrom(data: Record<string, unknown> | undefined): string[] {
  const names = (data?.source_names ?? data?.sourceNames) as unknown;
  if (!Array.isArray(names)) return [];
  return names.filter((n): n is string => typeof n === 'string');
}

export function CanonicalEntityList() {
  const {
    canonicals,
    selectedCanonical,
    filters,
    loading,
    entityTypes,
    ontologyDistribution,
    selectedIds,
    bulkClassifyRunning,
    setFilters,
    selectCanonical,
    fetchCanonicals,
    fetchMore,
    fetchEntityTypes,
    fetchOntologyDistribution,
    toggleSelection,
    clearSelection,
    classifySelected,
  } = useKnowledgeStore();

  const [searchParams] = useSearchParams();
  const uosClassFromUrl = searchParams.get('uos_class') ?? '';
  const entityTypeFromUrl = searchParams.get('entity_type') ?? '';

  useEffect(() => {
    // Seed filters from URL params (deep link from catalog pages), otherwise
    // just fetch with the current filters.
    if (uosClassFromUrl && uosClassFromUrl !== filters.ontologicalClass) {
      setFilters({ ontologicalClass: uosClassFromUrl });
    } else if (entityTypeFromUrl && entityTypeFromUrl !== filters.entityType) {
      setFilters({ entityType: entityTypeFromUrl });
    } else {
      fetchCanonicals();
    }
    fetchEntityTypes();
    fetchOntologyDistribution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uosClassFromUrl, entityTypeFromUrl]);

  const selectionCount = selectedIds.size;

  const totalOntologyCount = useMemo(
    () => ontologyDistribution.reduce((sum, r) => sum + r.count, 0),
    [ontologyDistribution],
  );

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      {/* Filters */}
      <div className="p-3 space-y-2 border-b border-slate-100">
        <select
          value={filters.entityType}
          onChange={(e) => setFilters({ entityType: e.target.value })}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
        >
          <option value="">All Entity Types</option>
          {entityTypes.map((t) => (
            <option key={t} value={t}>
              {formatLabel(t)}
            </option>
          ))}
        </select>
        <select
          value={filters.ontologicalClass}
          onChange={(e) => setFilters({ ontologicalClass: e.target.value })}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
        >
          <option value="">
            All UOS Classes{totalOntologyCount ? ` (${totalOntologyCount})` : ''}
          </option>
          {ontologyDistribution.map((row) => (
            <option key={row.id} value={row.id}>
              {formatLabel(row.id)} ({row.count})
            </option>
          ))}
        </select>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Semantic search..."
            value={filters.search}
            onChange={(e) => setFilters({ search: e.target.value })}
            className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg pl-8 pr-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30 placeholder-slate-400"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {selectionCount > 0 && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-brand-teal/5">
          <span className="text-xs text-navy-900">
            {selectionCount} selected
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={classifySelected}
              disabled={bulkClassifyRunning}
              className="flex items-center gap-1 text-xs font-medium text-brand-teal hover:text-brand-teal-dark disabled:opacity-60 px-2 py-1 rounded"
              title="Run ontological classification on selected entities"
            >
              {bulkClassifyRunning ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Sparkles size={12} />
              )}
              Classify Selected
            </button>
            <button
              onClick={clearSelection}
              className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading && canonicals.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">Loading...</div>
        ) : canonicals.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">
            No canonical entities found.
            {!loading && <p className="mt-1 text-xs">Make sure PostgreSQL is running.</p>}
          </div>
        ) : (
          <>
            {canonicals.map((c) => (
              <EntityRow
                key={c.id}
                entity={c}
                selected={selectedCanonical?.id === c.id}
                checked={selectedIds.has(c.id)}
                onClick={() => selectCanonical(c)}
                onToggleCheck={() => toggleSelection(c.id)}
              />
            ))}
            {canonicals.length >= 50 && !filters.search && !filters.ontologicalClass && (
              <button
                onClick={fetchMore}
                disabled={loading}
                className="w-full py-2 text-sm text-brand-teal hover:text-brand-teal-dark disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function EntityRow({
  entity,
  selected,
  checked,
  onClick,
  onToggleCheck,
}: {
  entity: CanonicalEntityData;
  selected: boolean;
  checked: boolean;
  onClick: () => void;
  onToggleCheck: () => void;
}) {
  const sources = sourceNamesFrom(entity.canonical_data);
  const subtitle = sources.slice(0, 3).join(' • ');
  const more = sources.length > 3 ? ` +${sources.length - 3}` : '';

  return (
    <div
      className={clsx(
        'group flex items-start gap-2 px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer',
        selected && 'bg-slate-50 border-l-2 border-l-brand-teal',
      )}
      onClick={onClick}
    >
      <label
        className="mt-1 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggleCheck}
          className="w-3.5 h-3.5 accent-brand-teal cursor-pointer"
        />
      </label>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">
          {entity.canonical_name}
          {entity.ontological_class && (
            <CheckCircle2
              size={11}
              className="inline-block ml-1.5 text-emerald-500"
              aria-label={`Classified as ${entity.ontological_class}`}
            />
          )}
        </div>
        {subtitle && (
          <div className="text-[11px] text-slate-400 truncate mt-0.5">
            {subtitle}
            {more}
          </div>
        )}
        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
          <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
            {entity.entity_type}
          </span>
          <span>{entity.source_entity_count} sources</span>
          <div
            className="ml-auto w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden"
            title={`Confidence: ${(entity.confidence * 100).toFixed(0)}%`}
          >
            <div
              className="h-full rounded-full bg-emerald-400"
              style={{ width: `${Math.max(0, Math.min(1, entity.confidence)) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
